import { AuthPillButton, Banner, StyledError, StyledInfo } from "../styled/Components";
import {
	AuthScreenAtom,
	FirebaseUser,
	IsCheckingOwnerName,
} from "../../../state/authAtoms";
import {
	GithubAuthProvider,
	GoogleAuthProvider,
	getAuth,
	signInWithPopup,
} from "firebase/auth";
import {
	addOwnerName,
	checkOwnerName,
	colors,
	strings,
	text,
} from "@quickpoint.me/shared";
import {
	clearOwnerNameFromSessionStorage,
	setOwnerNameToSessionStorage,
} from "../../../lib/ownerNameSessionStorage";
import {
	useRecoilCallback,
	useRecoilState,
	useRecoilValue,
	useSetRecoilState,
} from "recoil";

import { DemoMode } from "../../../state/uiAtoms";
import { track } from "../../../lib/metrics";
import { trackSignupConversion } from "../../../lib/conversionTracking";
import GithubIcon from "../../../icons/github-mark-white.svg?react";
import GoogleIcon from "../../../icons/web_dark_sq_na.svg?react";
import { IsInPortrait } from "../../../state/selectors";
import { LoginSuccessfulResetState } from "../../../state/authSelectors";
import SignUpWithEmail from "./SignUpWithEmail";
import { StyledForm } from "../LoginWithEmail";
import { StyledInput } from "../styled/StyledInput";
import { StyledWrapper } from "../styled/StyledWrapper";
import Turnstile from "react-turnstile";
import { cloneDeep } from "lodash-es";
import { getTurnstileSiteKey } from "../../../lib/getTurnstileSiteKey";
import { uploadRecord } from "./uploadRecord";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useOwnerNameAvailability } from "./useOwnerNameAvailability";

type OwnerNameFormValues = {
	ownerName: string;
};

export const TermsOfService = () => {
	return (
		<p>
			<a
				/* link teal: 7.2:1 on white (AAA) */
				style={{ color: colors.teal, fontSize: ".8rem" }}
				href="https://quickpoint.me/quickpoint/tncs"
				target="blank"
			>
				{text.auth.termsOfService}
			</a>
		</p>
	);
};

const SignUp = () => {
	const [accountCreated, setAccountCreated] = useState(false);
	const [createFirebaseAccount, setCreateFirebaseAccount] = useState(false);
	const [isCheckingOwnerName, setIsCheckingOwnerName] =
		useRecoilState(IsCheckingOwnerName);
	const [networkError, setNetworkError] = useState(false);
	const [ownerNameExists, setOwnerNameExists] = useState(false);
	const [authScreen, setAuthScreen] = useRecoilState(AuthScreenAtom);
	const setDemoMode = useSetRecoilState(DemoMode);
	const setFireBaseUser = useSetRecoilState(FirebaseUser);
	const setLoginSuccessfulResetState = useSetRecoilState(
		LoginSuccessfulResetState,
	);
	const isInPortrait = useRecoilValue(IsInPortrait);
	const [turnstileToken, setTurnstileToken] = useState<string>();
	const [disabled, setDisabled] = useState(true);

	const {
		register,
		watch,
		formState: { errors },
	} = useForm<OwnerNameFormValues>({ mode: "onChange" });

	let ownerName = watch("ownerName");
	const nameAvailability = useOwnerNameAvailability(ownerName);

	// a stale "name taken" from an earlier submit clears as soon as the user edits
	useEffect(() => {
		setOwnerNameExists(false);
	}, [ownerName]);

	const requestPopUpToSignUp = useRecoilCallback(
		() =>
			async (
				name: string,
				provider: GoogleAuthProvider | GithubAuthProvider,
			) => {
				if (!turnstileToken) return;
				const auth = getAuth();
				const result = await signInWithPopup(auth, provider);
				// The signed-in user info.
				const user = result.user;
				// This gives you a Google Access Token.
				const credential = GoogleAuthProvider.credentialFromResult(result);

				if (credential) {
					try {
						const jwt = await auth.currentUser?.getIdToken();
						if (!jwt) throw Error("SignUp: Bad JWT");

						const res = await addOwnerName({
							owner: name,
							jwt,
							turnstileToken,
						});
						if (res.success) {
							const recoilUser = cloneDeep(user);
							setAccountCreated(true);
							setFireBaseUser(recoilUser);
							setLoginSuccessfulResetState(true);
							setDemoMode(true);
							// Track signup event
							track(user.uid, 'signup', { provider: provider.providerId, ownerName: name });
							// Send conversion to Meta & Reddit via CAPI
							trackSignupConversion(user.email ?? undefined);
							if (user.email) {
								uploadRecord(user.email);
							}
						} else {
							// name lost between check and register, or the account already has one
							clearOwnerNameFromSessionStorage();
							setCreateFirebaseAccount(false);
							if (res.message === strings.workers.ownerExists) {
								setOwnerNameExists(true);
							} else {
								setNetworkError(true);
							}
						}
					} catch (e) {
						console.error("SignUp: ", e);
						clearOwnerNameFromSessionStorage();
					}
				} else {
					clearOwnerNameFromSessionStorage();
				}
			},
		[turnstileToken],
	);

	const chooseGoogle = (name: string) => {
		const provider = new GoogleAuthProvider();
		provider.addScope("profile");
		provider.addScope("email");
		requestPopUpToSignUp(name, provider);
	};

	const chooseGithub = (name: string) => {
		const provider = new GithubAuthProvider();
		requestPopUpToSignUp(name, provider);
	};

	const signUpWith = async (
		method: (typeof strings.signUpMethods)["email" | "google" | "github"],
	) => {
		if (!turnstileToken) return;
		if (!ownerName) return;
		setOwnerNameExists(false);
		if (method === strings.signUpMethods.email) {
			// check before moving on — finding out the name is taken after
			// typing an email and password is too late
			setIsCheckingOwnerName(true);
			const checkName = await checkOwnerName(ownerName.toLowerCase());
			setIsCheckingOwnerName(false);
			if (checkName.networkError) {
				setNetworkError(true);
				return;
			}
			if (!checkName.success) {
				setOwnerNameExists(true);
				return;
			}
			setAuthScreen("signUpEmail");
			return;
		}

		setIsCheckingOwnerName(true);
		// advisory availability check — /register (in requestPopUpToSignUp) is the authority
		ownerName = ownerName.toLowerCase();
		const checkName = await checkOwnerName(ownerName);
		if (checkName.networkError) {
			setNetworkError(true);
			setIsCheckingOwnerName(false);
			return;
		}
		if (checkName.success) {
			setOwnerNameExists(false);
			setIsCheckingOwnerName(false);
			setCreateFirebaseAccount(true);

			// set this here. Firebase onAuth fires before the register call resolves,
			// so it has to be set before being sure the signup process has completed.
			// It is cleared if there's an error
			setOwnerNameToSessionStorage(ownerName);

			if (method === strings.signUpMethods.google) chooseGoogle(ownerName);
			if (method === strings.signUpMethods.github) chooseGithub(ownerName);
		} else {
			setIsCheckingOwnerName(false);
			setOwnerNameExists(true);
		}
	};

	const goBack = useRecoilCallback(({ set }) => () => {
		set(AuthScreenAtom, "landing");
	});

	if (authScreen === "signUpEmail") {
		return <SignUpWithEmail ownerName={ownerName} />;
	}

	return (
		<StyledInput $isInPortrait={isInPortrait}>
			<Banner />
			<StyledInfo className="info">
				<p className="pick-account-name">
					{text.auth.pickNamePrompt}
				</p>
			</StyledInfo>
			<div className="form">
				<StyledForm>
					<form>
						<StyledWrapper>
							<label className="owner-name-label" htmlFor="ownerName">
								{text.auth.nameLabel}
							</label>
							<input
								aria-invalid={!!errors.ownerName}
								placeholder={text.auth.namePlaceholder}
								className="auth-input auth-owner-name-input input-box-as-line"
								onKeyDown={(e) => {
									if (e.key === "Enter") e.preventDefault();
								}}
								id="ownerName"
								autoComplete="off"
								inputMode="text"
								autoCorrect="off"
								spellCheck={false}
								type="text"
								{...register("ownerName", {
									required: true,
									maxLength: 100,
									minLength: 3,
									pattern: /^[a-zA-Z0-9_-]*$/i,
								})}
							/>
						</StyledWrapper>
						<StyledError role="alert">
							{errors.ownerName && errors.ownerName.type === "minLength" && (
								<span>{text.auth.nameTooShort}</span>
							)}
							{errors.ownerName && errors.ownerName.type === "maxLength" && (
								<span role="alert">{text.auth.nameTooLong}</span>
							)}
							{errors.ownerName && errors.ownerName.type === "pattern" && (
								<span role="alert">
									{text.auth.nameInvalidChars}
								</span>
							)}
							{isCheckingOwnerName && (
								<span>{text.auth.checkingName}</span>
							)}
							{createFirebaseAccount && (
								<span>{text.auth.nameAvailable}</span>
							)}
							{accountCreated && <span>{text.auth.accountReady}</span>}
							{(ownerNameExists || nameAvailability === "taken") && (
								<span>{text.auth.nameTaken}</span>
							)}
							{!ownerNameExists &&
								!isCheckingOwnerName &&
								!createFirebaseAccount &&
								!accountCreated && (
									<>
										{nameAvailability === "checking" && (
											<span aria-live="polite">{text.auth.checkingName}</span>
										)}
										{nameAvailability === "available" && (
											<span aria-live="polite">{text.auth.nameFree}</span>
										)}
									</>
								)}
						</StyledError>
					</form>
				</StyledForm>
				{networkError && (
					<StyledError>
						{text.auth.registerError}
					</StyledError>
				)}
			</div>

			<StyledInfo className="more-info">
				<p>
					{text.auth.storiesUrlPrefix}
					<span className="pick-account-name-live">{ownerName}</span>
				</p>
			</StyledInfo>
			<div className="buttons">
				<Turnstile
					style={{
						transition: disabled ? "none" : "opacity 1s ease-out",
						opacity: disabled ? 1 : 0.3,
					}}
					sitekey={getTurnstileSiteKey()}
					theme="light"
					fixedSize={true}
					retry="auto"
					refreshExpired="auto"
					onVerify={(token) => {
						setTurnstileToken(token);
						setDisabled(false);
					}}
					onError={() => {
						// transient challenge failures (common under Safari ITP) —
						// drop the token and let retry="auto" re-run the challenge
						setTurnstileToken(undefined);
						setDisabled(true);
					}}
					onExpire={() => {
						setTurnstileToken(undefined);
						setDisabled(true);
					}}
				/>
				<AuthPillButton
					disabled={disabled || nameAvailability === "taken"}
					onClick={() => signUpWith(strings.signUpMethods.email)}
				>
					{text.auth.continueEmail}
				</AuthPillButton>
				<AuthPillButton
					$variant="dark"
					disabled={disabled || nameAvailability === "taken"}
					onClick={() => signUpWith(strings.signUpMethods.google)}
				>
					<GoogleIcon aria-hidden="true" focusable="false" />
					{text.auth.continueGoogle}
				</AuthPillButton>
				<AuthPillButton
					$variant="dark"
					disabled={disabled || nameAvailability === "taken"}
					onClick={() => signUpWith(strings.signUpMethods.github)}
				>
					<GithubIcon aria-hidden="true" focusable="false" />
					{text.auth.continueGithub}
				</AuthPillButton>
				<AuthPillButton $variant="primaryOutline" onClick={() => goBack()}>
					{text.auth.back}
				</AuthPillButton>

				<TermsOfService />
			</div>
		</StyledInput>
	);
};

export default SignUp;
